import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  ReactNode,
  FC,
} from 'react'
import { useContentQuery } from '../hooks/useContentQuery'
import { useQuery } from '@based/sdk'
import useLocalStorage from '@based/use-local-storage'

export type ThemeVariant = 'light' | 'dark'
export type ThemeVariantProp = ThemeVariant | 'auto'

// make something to check if it's the preview mode / colors

export type ThemeColors = {
  primaryAccent: string
  secondaryAccent: string
  background: string
  outline: string
  border: string
  textPrimary: string
  textSecondary: string
  textTertiary: string
  error: string
}

export type Theme = {
  variant: ThemeVariant
  colors: ThemeColors
  toggleVariant: () => void
  imageGradient?: boolean
  textGradient?: boolean
}

const STORAGE_KEY = '$theme_variant'

const BASE_COLORS: ThemeColors = {
  primaryAccent: 'rgba(174,43,171,1)',
  secondaryAccent: 'rgba(36,163,199,1)',
  background: 'rgba(255,255,255,1)',
  // for input fields
  outline: '#ae2bac38',
  border: 'rgba(0,0,0,0.1)', // 10%
  textPrimary: 'rgba(0, 0, 0, 0.90)',
  textSecondary: 'rgba(0, 0, 0, 0.60)',
  textTertiary: 'rgba(0, 0, 0, 0.30)',
  error: '#FF0000',
}

const createColors = (
  variant: ThemeVariant,
  editionTheme?: Partial<Omit<ThemeColors, 'background'>> | null,
): ThemeColors => {
  // const background = variant === 'dark' ? '#000000' : '#FFFFFF'

  const filteredTheme: Partial<Omit<ThemeColors, 'background'>> = {}
  if (editionTheme) {
    for (const key of Object.keys(editionTheme) as Array<
      keyof typeof editionTheme
    >) {
      const value = editionTheme[key]
      if (value && value !== '') {
        filteredTheme[key] = value
      }
    }
  }

  return {
    ...BASE_COLORS,
    ...filteredTheme,
    // background,
  }
}

const getSystemVariant = (): ThemeVariant => {
  if (typeof window === 'undefined' || !window.matchMedia) {
    return 'light'
  }
  return window.matchMedia('(prefers-color-scheme: dark)').matches
    ? 'dark'
    : 'light'
}

const getInitialVariant = (propVariant?: ThemeVariantProp): ThemeVariant => {
  if (typeof window !== 'undefined') {
    try {
      const stored = window.localStorage.getItem(
        STORAGE_KEY,
      ) as ThemeVariant | null
      if (stored === 'light' || stored === 'dark') {
        return stored
      }
    } catch {}
  }

  if (propVariant === 'light' || propVariant === 'dark') {
    return propVariant
  }

  return getSystemVariant()
}

const COLOR_VAR_ENTRIES: [keyof ThemeColors, `--${string}`][] = [
  ['primaryAccent', '--primary-accent'],
  ['secondaryAccent', '--secondary-accent'],
  ['background', '--background'],
  ['outline', '--outlines'],
  ['textPrimary', '--primary-text'],
  ['textSecondary', '--secondary-text'],
  ['textTertiary', '--tertiary-text'],
  ['error', '--error'],
]

const applyCssVariables = (colors: ThemeColors) => {
  if (typeof document === 'undefined') return
  const rootStyle = document.documentElement.style

  for (const [colorKey, cssVarName] of COLOR_VAR_ENTRIES) {
    rootStyle.setProperty(cssVarName, colors[colorKey])
  }
}

const ThemeContext = createContext<Theme | null>(null)

export const useTheme = (): Theme => {
  const ctx = useContext(ThemeContext)

  if (!ctx) {
    throw new Error('useTheme must be used within ThemeProvider')
  }
  return ctx
}

type ThemeProviderProps = {
  children: ReactNode
  variant?: ThemeVariantProp
}

export const ThemeProvider: FC<ThemeProviderProps> = ({
  children,
  variant: propVariant = 'auto',
}) => {
  const [variant, setVariant] = useState<ThemeVariant>(() =>
    getInitialVariant(propVariant),
  )

  const [storageActiveLanguage] = useLocalStorage('language')
  const editionWindow =
    typeof window !== 'undefined' ? (window as any)?.DATA?.edition : undefined
  const detectedContent =
    typeof window !== 'undefined' &&
    window.location.pathname.includes('/preview')
      ? 'preview-content'
      : 'live-content'

  const { data: theme = {} } = useQuery(
    editionWindow !== undefined ? detectedContent : '',
    {
      id: editionWindow,
      content: 'theme',
      lang: storageActiveLanguage ?? 'en',
    },
  )

  useEffect(() => {
    if (propVariant === 'light' || propVariant === 'dark') {
      setVariant(propVariant)
    }
  }, [propVariant])

  useEffect(() => {
    if (propVariant !== 'auto') return
    if (typeof window === 'undefined' || !window.matchMedia) return

    const media = window.matchMedia('(prefers-color-scheme: dark)')
    const listener = (event: MediaQueryListEvent) => {
      const next: ThemeVariant = event.matches ? 'dark' : 'light'
      setVariant(next)
      try {
        window.localStorage.setItem(STORAGE_KEY, next)
      } catch {}
    }

    media.addEventListener('change', listener)
    return () => media.removeEventListener('change', listener)
  }, [propVariant])

  useEffect(() => {
    if (typeof window === 'undefined') return
    try {
      window.localStorage.setItem(STORAGE_KEY, variant)
    } catch {}
  }, [variant])

  const colors = useMemo(() => createColors(variant, theme), [variant, theme])

  useEffect(() => {
    applyCssVariables(colors)
  }, [colors])

  const toggleVariant = () => {
    setVariant((current) => (current === 'light' ? 'dark' : 'light'))
  }

  const value: Theme = useMemo(
    () => ({
      variant,
      colors,
      toggleVariant,
      textGradient: theme.textGradient,
      imageGradient: theme.imageGradient,
    }),
    [variant, colors, theme.textGradient, theme.imageGradient],
  )

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
}
